import CryptoJS from 'crypto-js';
import api, { API_VERSION, ValidateAxiosError } from '@/modules/api.service';
import { AuthToken, SingleInputDto } from '@/api.types';
import { Endpoints } from '@/shared/constants/endpoints';
import { PERMISSOES_USUARIO, TOKEN_KEY } from '@/shared/constants/strings';
import { RoleLevelDto } from '../../papeis/role-level.dto';
import { SECRET_KEY_PERMISSOES } from '@/shared/constants/strings';
import { PermissionEnum } from '@/modules/system/papeis/enum/permission.enum';
import { RoleEnum } from '@/modules/system/papeis/enum/role.enum';

/**
 * Efetua login no sistema.
 * @param usuario Nome de usuário
 * @param senha Senha.
 * @returns Estado.
 */
export async function Logar(usuario: string, senha: string) {
  return api
    .post(
      `${Endpoints.AUTH}`,
      {
        username: usuario,
        password: senha,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Version: API_VERSION,
        },
      },
    )
    .then((res) => {
      setLoginTokenOnStorage((res.data as AuthToken).value);
      return res.data;
    })
    .catch((error) => {
      return ValidateAxiosError(error, error.response.data.message);
    });
}

export async function LogarComGoogle() {
  return api
    .get('auth/google', { headers: { 'Content-Type': 'application/json' } })
    .then((res) => {
      setLoginTokenOnStorage(res.data);
      return res.data;
    })
    .catch((error) => {
      return ValidateAxiosError(error, error.response.data.message);
    });
}

/**
 * Troca a senha do usuário (fluxo recuperação por código).
 * @param senha nova senha.
 * @param codigorecuperacao código de recuperação.
 * @returns void.
 */
export async function TrocarSenha(senha: string, codigorecuperacao: string) {
  return api
    .patch(
      Endpoints.AUTH + '/trocarsenha',
      { senha: senha, codigorecuperacao: codigorecuperacao },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return ValidateAxiosError(error, 'Falha ao trocar senha.');
    });
}

/**
 * Altera a senha do usuário logado (senha atual + nova senha).
 */
export async function AlterarSenha(senhaAtual: string, senhaNova: string) {
  return api
    .patch(
      Endpoints.AUTH + '/alterar-senha',
      { senhaAtual, senhaNova },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((response) => response)
    .catch((error) => {
      const message = error.response?.data?.message ?? 'Falha ao alterar senha.';
      return ValidateAxiosError(error, message);
    });
}

export async function requestPasswordRecoveryCode(email: string) {
  const input: SingleInputDto = { input: email }

  return api
    .post(Endpoints.AUTH + '/recuperar', input, { headers: { 'Content-Type': 'application/json' } })
    .then((res) => { return res })
    .catch((error) => { return error });
}

/**
 * Sai do sistema.
 */
export function Deslogar() {
  localStorage.clear();
}

/**
 * Carrega as permissões do token.
 * @returns
 */
export function CarregarPermissoes() {
  try {

    const bytes = CryptoJS.AES.decrypt(localStorage.getItem(PERMISSOES_USUARIO) as string, SECRET_KEY_PERMISSOES);
    if (bytes) {

      const permissoes = JSON.parse(bytes.toString(CryptoJS.enc.Utf8)) as RoleLevelDto[];
      return permissoes;
    }
    return [];
  } catch (error) {
    return [];
  }
}

/**
 * Valida se o usuário tem determinada permissão.
 * @param disponiveis Lista com as permissões que o usuário possui.
 * @param necessarias Permissões requisitadas pela interface.
 * @returns True or False.
 */
export function ValidarPermissao(disponiveis: RoleLevelDto[], necessarias: RoleLevelDto[]) {
  return necessarias.every((necessaria) => {
    const disponivel = disponiveis.find(d => Number(d.role) === Number(necessaria.role) && Number(d.level) >= Number(necessaria.level));
    return disponivel;
  });
}


/**
 * Define o token no sistema.
 * @param token Token com permissões.
 */
export function setLoginTokenOnStorage(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Busca o token de login.
 * @returns O token de login.
 */
export function getLoginTokenFromStorage() {
  return localStorage.getItem(TOKEN_KEY);
}

type LoginJwtPayload = {
  userId?: number | null;
  personCPF?: string;
  attendanceSheetId?: string;
};

function parseLoginJwtPayload(): LoginJwtPayload | null {
  const raw = getLoginTokenFromStorage();
  if (!raw || typeof raw !== 'string' || !raw.includes('.')) return null;
  const payloadB64 = raw.split('.')[1];
  if (!payloadB64) return null;
  try {
    const normalized = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (normalized.length % 4)) % 4;
    const json = atob(normalized + '='.repeat(pad));
    return JSON.parse(json) as LoginJwtPayload;
  } catch {
    return null;
  }
}

/**
 * JWT sem `userId` numérico (login por link/token da folha), distinto do usuário do sistema.
 * Exige `personCPF` no payload para não confundir com outros fluxos sem userId.
 */
export function isJwtSessaoSemUsuarioId(): boolean {
  const payload = parseLoginJwtPayload();
  if (!payload) return false;
  const uid = payload.userId;
  if (uid != null && Number.isFinite(Number(uid))) return false;
  if (!payload.personCPF || String(payload.personCPF).trim() === '') return false;
  return true;
}

/** CPF apenas dígitos para comparação. */
function cpfSoDigitos(cpf: string | null | undefined): string {
  return String(cpf ?? '').replace(/\D/g, '');
}

/**
 * Papéis típicos de quem acessa a folha como servidor (defensor, gestão de folha, usuário do sistema etc.).
 * Estagiário só por link costuma vir com `SetPermissoes('')` e sem esses papéis.
 */
function temPapelServidorRelacionadoFolha(permissoes: RoleLevelDto[]): boolean {
  return (
    ValidarPermissao(permissoes, [{ role: RoleEnum.FOLHA, level: PermissionEnum.READ }]) ||
    ValidarPermissao(permissoes, [{ role: RoleEnum.DEFENSOR, level: PermissionEnum.READ }]) ||
    ValidarPermissao(permissoes, [{ role: RoleEnum.USUARIO, level: PermissionEnum.READ }]) ||
    ValidarPermissao(permissoes, [{ role: RoleEnum.COLABORADOR, level: PermissionEnum.READ }]) ||
    ValidarPermissao(permissoes, [{ role: RoleEnum.ADMIN_ACTIONS, level: PermissionEnum.READ }])
  );
}

/**
 * Listagem de folhas (`/folhas`, pendências): oculta "Escolha de módulo" / breadcrumb ampliado só no fluxo
 * **estagiário por link** (JWT sem userId, sem login de usuário e sem papéis de servidor).
 * Defensores e usuários do sistema mantêm os menus.
 */
export function deveOcultarMenusCompletosListagemFolha(username: string | null | undefined): boolean {
  if (!isJwtSessaoSemUsuarioId()) return false;
  if (String(username ?? '').trim()) return false;
  if (temPapelServidorRelacionadoFolha(CarregarPermissoes())) return false;
  return true;
}

type FolhaMinCpf = { estagiario?: { cpf?: string | null } } | null | undefined;

/**
 * Edição da folha: mesma regra da listagem e, se já houver folha carregada, o CPF do JWT deve ser o do **estagiário titular**.
 * Assim supervisores/defensores com JWT sem `userId` não perdem "Folhas" / "Escolha de módulo" / Voltar.
 */
export function deveOcultarMenusCompletosEdicaoFolha(
  username: string | null | undefined,
  folha: FolhaMinCpf,
): boolean {
  if (!deveOcultarMenusCompletosListagemFolha(username)) return false;
  if (!folha?.estagiario?.cpf) return true;
  const payload = parseLoginJwtPayload();
  const t = cpfSoDigitos(payload?.personCPF);
  const e = cpfSoDigitos(folha.estagiario.cpf);
  if (!t || !e) return true;
  return t === e;
}

/** `attendanceSheetId` embutido no JWT (envio de link), se existir. */
export function getAttendanceSheetIdFromJwt(): string | undefined {
  const id = parseLoginJwtPayload()?.attendanceSheetId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/**
 * CPF (`personCPF`) só com dígitos a partir do JWT em `TOKEN_KEY`.
 * Útil na sessão por token (sem `username`) para validar cadastro de defensor na API.
 */
export function getPersonCpfDigitosFromLoginJwt(): string | undefined {
  const d = cpfSoDigitos(parseLoginJwtPayload()?.personCPF);
  return d.length >= 11 ? d : undefined;
}

/**
 * Verifica se está logado.
 * @returns true or false.
 */
export function isLogedIn() {
  const token = getLoginTokenFromStorage();
  return typeof token !== 'undefined' && token != null;
}

/**
 * Permissões do usuário.
 * @param encrypted Permissões do usuário
 */
export function SetPermissoes(encrypted: string) {
  localStorage.setItem(PERMISSOES_USUARIO, encrypted);
}
